/** Horizontal heading (yaw, radians; 0 = +z, the direction `Object3D.lookAt` turns +z to) for every
    projectile on screen. The shot's own aim (`dir`/`targetPos`, offline only) wins when known; otherwise
    — online and in replays, where the wire carries neither — the heading comes from the projectile's own
    motion between frames. Keyed by projectile id, not by view object: identity is only as stable as the
    GameView implementation makes it, the id always is. Never pitches: y is ignored throughout. */
export function createHeadings() {
  const tracks = new Map<number, { x: number; z: number; yaw: number | null; seen: number }>();
  let frame = 0;

  /** Yaw for projectile `id` now at `pos`, or null while it has neither an aim nor moved yet. */
  function heading(id: number, pos: { x: number; z: number }, aim?: { x: number; z: number } | null): number | null {
    let tr = tracks.get(id);
    if (!tr) { tr = { x: pos.x, z: pos.z, yaw: null, seen: frame }; tracks.set(id, tr); }
    const dx = pos.x - tr.x, dz = pos.z - tr.z;
    if (aim && (aim.x !== 0 || aim.z !== 0)) tr.yaw = Math.atan2(aim.x, aim.z);
    // A frame with no movement (a repeated snapshot, a paused clock) keeps the last heading.
    else if (dx * dx + dz * dz > 1e-8) tr.yaw = Math.atan2(dx, dz);
    tr.x = pos.x; tr.z = pos.z; tr.seen = frame;
    return tr.yaw;
  }
  /** Ends a frame: forgets every projectile `heading()` was not asked about during it. */
  function sweep() {
    for (const [id, tr] of tracks) if (tr.seen !== frame) tracks.delete(id);
    frame++;
  }
  return { heading, sweep, get size() { return tracks.size; } };
}
